'use strict';

/**
 * Presentation metadata keyed by PortalApi registry node id.
 * Live bind keys live on the API graph DTO; this file must not define factMatch.
 */

import {
	CORE_PROGRESS_STAGE_IDS,
	KITS_PROGRESS_STAGE_IDS,
	MERGED_PROGRESS_STAGE_IDS,
	PIPELINE_PROGRESS_STAGE_IDS,
} from './pipelineProgressCopy';

/** @typedef {{ description: string, dependsOn: string[], highLevelStage: string, scope: string }} PipelineStatusNodePresentation */

/** @type {Readonly<Record<string, PipelineStatusNodePresentation>>} */
export const PIPELINE_STATUS_GRAPH_PRESENTATION_BY_ID = Object.freeze({
	'v7-project-poller': {
		scope: 'Project',
		description: 'Publishing trigger activated from mlims',
		dependsOn: [],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.received,
	},
	'v7-project-available': {
		scope: 'Project',
		description: 'Transferring files from mlims',
		dependsOn: ['v7-project-poller'],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.transferringData,
	},
	'v7-sample-set-available': {
		scope: 'Project',
		description: 'Transferring sample set from mlims',
		dependsOn: ['v7-project-poller'],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.transferringData,
	},
	'v7-product-sample-set-biovisservice': {
		scope: 'Project',
		description: 'Sending mlims sample set to IBP',
		dependsOn: ['v7-sample-set-available'],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.preparingResults,
	},
	'v7-product-sample-set-portalapi': {
		scope: 'Project',
		description: 'Storing mlims sample set in database',
		dependsOn: ['v7-sample-set-available'],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.preparingResults,
	},
	'v7-product-sample-set-pathwayexplorerapi': {
		scope: 'Project',
		description: 'Storing mlims sample set in database',
		dependsOn: ['v7-sample-set-available'],
		highLevelStage: PIPELINE_PROGRESS_STAGE_IDS.preparingResults,
	},
	'core-stats-completed': {
		scope: 'Project',
		description: 'ProductService StatsCompleted',
		dependsOn: [],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.preparingResults,
	},
	'core-portal-pssa': {
		scope: 'Project',
		description: 'PortalApi ProductSampleSetAvailable',
		dependsOn: ['core-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'core-pathway': {
		scope: 'Project',
		description: 'PathwayExplorerApi ProductSampleSetAvailable',
		dependsOn: ['core-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'core-heatmap': {
		scope: 'Project',
		description: 'HeatmapGenerationToolApi ProductSampleSetAvailable',
		dependsOn: ['core-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'core-biovis': {
		scope: 'Project',
		description: 'BioVisService ProductSampleSetAvailable',
		dependsOn: ['core-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'dp-stats-completed': {
		scope: 'Project',
		description: 'ProductService StatsCompleted',
		dependsOn: [],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.preparingResults,
	},
	'dp-portal-pssa': {
		scope: 'Project',
		description: 'PortalApi ProductSampleSetAvailable',
		dependsOn: ['dp-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'dp-smartpanel': {
		scope: 'Project',
		description: 'SmartPanelDataService ProductSampleSetAvailable',
		dependsOn: ['dp-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'dp-discovery-panels': {
		scope: 'Project',
		description: 'DiscoveryPanelsApi ProductSampleSetAvailable',
		dependsOn: ['dp-stats-completed'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'dp-smartpanel-viz': {
		scope: 'Project',
		description: 'DiscoveryPanelsApi ProductSmartPanelVisualizationDataAvailable',
		dependsOn: ['dp-smartpanel'],
		highLevelStage: CORE_PROGRESS_STAGE_IDS.publishing,
	},
	'kits-manifest-available': {
		scope: 'Project',
		description: 'PortalApi ManifestAvailable',
		dependsOn: [],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.received,
	},
	'kits-product-project-available': {
		scope: 'Project',
		description: 'ProductService ProjectAvailable',
		dependsOn: ['kits-manifest-available'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.received,
	},
	'kits-portal-project-available': {
		scope: 'Project',
		description: 'PortalApi ProjectAvailable',
		dependsOn: ['kits-product-project-available'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.received,
	},
	'kits-datanorm-completed': {
		scope: 'Project',
		description: 'ProductService DataNormCompleted',
		dependsOn: ['kits-manifest-available'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.preparingResults,
	},
	'kits-portal-pssa': {
		scope: 'Project',
		description: 'PortalApi ProductSampleSetAvailable',
		dependsOn: ['kits-datanorm-completed'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.publishing,
	},
	'kits-pathway': {
		scope: 'Project',
		description: 'PathwayExplorerApi ProductSampleSetAvailable',
		dependsOn: ['kits-datanorm-completed'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.publishing,
	},
	'kits-biovis': {
		scope: 'Project',
		description: 'BioVisService ProductSampleSetAvailable',
		dependsOn: ['kits-datanorm-completed'],
		highLevelStage: KITS_PROGRESS_STAGE_IDS.publishing,
	},
	'merged-merge-projects': {
		scope: 'Project',
		description: 'PortalApi MergeProjects',
		dependsOn: [],
		highLevelStage: MERGED_PROGRESS_STAGE_IDS.received,
	},
	'merged-project-available': {
		scope: 'Project',
		description: 'PortalApi ProjectAvailable',
		dependsOn: ['merged-merge-projects'],
		highLevelStage: MERGED_PROGRESS_STAGE_IDS.publishing,
	},
});

export function pipelineStatusPresentationNodeIds() {
	return Object.keys(PIPELINE_STATUS_GRAPH_PRESENTATION_BY_ID);
}
